import React from "react";
import { SectionContentProps } from "../../types";
import EditableText from "../../core/EditableText";
import EditableImage from "../../core/EditableImage";
import {
    PiPlus,
    PiTrash,
    PiLinkedinLogo,
    PiTwitterLogo,
    PiInstagramLogo,
    PiGlobe,
    PiEnvelope
} from "react-icons/pi";

interface TeamMember {
    id: string;
    name: string;
    role: string;
    bio: string;
    image: string;
    email?: string;
    linkedin?: string;
    twitter?: string;
    instagram?: string;
    website?: string;
}

export default function TeamHorizontalCards({ content, onContentChange, isSelected }: SectionContentProps) {
    const fontFamily = content.fontFamily || "'Inter', sans-serif";

    const members: TeamMember[] = content.members || [
        {
            id: "1",
            name: "Sarah Johnson",
            role: "CEO & Founder",
            bio: "Visionary leader with 15+ years of experience in technology and innovation.",
            image: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=400&h=400&fit=crop",
            email: "sarah@example.com",
            linkedin: "https://linkedin.com",
            twitter: "https://twitter.com"
        },
        {
            id: "2",
            name: "Michael Chen",
            role: "CTO",
            bio: "Tech enthusiast passionate about building scalable solutions.",
            image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=400&fit=crop",
            email: "michael@example.com",
            linkedin: "https://linkedin.com"
        },
        {
            id: "3",
            name: "Emily Rodriguez",
            role: "Head of Design",
            bio: "Award-winning designer creating beautiful user experiences.",
            image: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=400&h=400&fit=crop",
            email: "emily@example.com",
            instagram: "https://instagram.com"
        }
    ];

    const addMember = () => {
        const newMember = {
            id: Date.now().toString(),
            name: "New Team Member",
            role: "Role Title",
            bio: "Brief description of this team member.",
            image: "",
            email: "",
            linkedin: "",
            twitter: ""
        };
        onContentChange?.({ ...content, members: [...members, newMember] });
    };

    const removeMember = (idx: number) => {
        const newMembers = members.filter((_: any, i: number) => i !== idx);
        onContentChange?.({ ...content, members: newMembers });
    };

    const updateMember = (idx: number, field: string, value: string) => {
        const newMembers = [...members];
        newMembers[idx] = { ...newMembers[idx], [field]: value };
        onContentChange?.({ ...content, members: newMembers });
    };

    return (
        <div className={`bg-white px-8 py-20 ${isSelected ? "ring-2 ring-blue-500" : ""}`} style={{ fontFamily }}>
            <div className="mx-auto max-w-5xl">
                <div className="mb-16 text-center">
                    <h2 className="mb-4 text-4xl font-bold text-gray-900">
                        <EditableText
                            value={content.title || "Our Leadership Team"}
                            onChange={(val) => onContentChange?.({ ...content, title: val })}
                            as="span"
                            placeholder="Section Title"
                        />
                    </h2>
                    <p className="mx-auto max-w-2xl text-lg text-gray-600">
                        <EditableText
                            value={content.subtitle || "Guiding our company towards new heights"}
                            onChange={(val) => onContentChange?.({ ...content, subtitle: val })}
                            as="span"
                            placeholder="Section subtitle"
                        />
                    </p>
                </div>
                <div className="space-y-6">
                    {members.map((member, idx) => (
                        <div key={member.id || idx} className="group relative flex flex-col md:flex-row bg-white rounded-2xl shadow-sm border border-gray-100 hover:shadow-lg overflow-hidden">
                            {isSelected && (
                                <button onClick={(e) => { e.stopPropagation(); removeMember(idx); }} className="absolute -top-3 -right-3 hidden group-hover:flex h-8 w-8 items-center justify-center rounded-full bg-red-100 text-red-500 z-10">
                                    <PiTrash className="h-4 w-4" />
                                </button>
                            )}
                            <div className="w-full md:w-72 h-64 md:h-auto flex-shrink-0">
                                <EditableImage value={member.image} onChange={(val) => updateMember(idx, "image", val)} alt={member.name} className="h-full w-full object-cover" width={300} height={400} />
                            </div>
                            <div className="flex-1 p-8 flex flex-col justify-center">
                                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                                    <EditableText value={member.name} onChange={(val) => updateMember(idx, "name", val)} as="span" placeholder="Name" />
                                </h3>
                                <p className="text-sm font-semibold text-blue-600 mb-4 uppercase tracking-wide">
                                    <EditableText value={member.role} onChange={(val) => updateMember(idx, "role", val)} as="span" placeholder="Role" />
                                </p>
                                <p className="text-gray-600 leading-relaxed">
                                    <EditableText value={member.bio} onChange={(val) => updateMember(idx, "bio", val)} as="span" placeholder="Bio" />
                                </p>
                                <div className="flex items-center gap-3 mt-6 pt-6 border-t border-gray-100">
                                    {member.email && <a href={`mailto:${member.email}`} className="p-2 text-gray-400 hover:text-blue-600"><PiEnvelope className="w-5 h-5" /></a>}
                                    {member.linkedin && <a href={member.linkedin} target="_blank" className="p-2 text-gray-400 hover:text-blue-700"><PiLinkedinLogo className="w-5 h-5" /></a>}
                                    {member.twitter && <a href={member.twitter} target="_blank" className="p-2 text-gray-400 hover:text-blue-500"><PiTwitterLogo className="w-5 h-5" /></a>}
                                </div>
                            </div>
                        </div>
                    ))}
                    {isSelected && (
                        <button onClick={addMember} className="w-full flex flex-col items-center justify-center py-8 rounded-2xl border-2 border-dashed border-gray-300 bg-gray-50 text-gray-400 hover:border-blue-400 hover:bg-blue-50 hover:text-blue-500">
                            <PiPlus className="h-10 w-10 mb-2" />
                            <span className="text-lg font-medium">Add Team Member</span>
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}
